// src/preferences-store.ts
//
// Preferências de UI persistidas (Zustand + localStorage): tema, escala de fonte,
// idioma e modo de estudo. O "Ver como" (view-as) é apenas da sessão atual.
// O view-as injeta o header x-act-as em TODA chamada
// (query+mutação) no api.ts; o back (resolveSession) SÓ aceita de um admin e
// apenas para REBAIXAR para um papel inferior — reavalia papel+scopes como o
// alvo, então a visão E a escrita ficam restritas de verdade (não é placebo).
// O view-as é deliberadamente efêmero: recarregar ou iniciar outra sessão volta
// sempre ao papel real, evitando que um admin pareça ter perdido permissões.
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { OrgRole } from "@/api";

// 3 temas (spec DESIGN 300): claro, escuro e "startup" (família-escura, energia
// de operação). Seleção via data-theme + classe .dark sincronizada — ver theme.tsx.
export type Theme = "light" | "dark" | "startup";
export const THEMES: Theme[] = ["light", "dark", "startup"];
export const THEME_LABELS: Record<Theme, string> = {
  light: "Claro",
  dark: "Escuro",
  startup: "Startup",
};
// Temas família-escura: recebem a classe .dark (para os utilitários dark:).
export const DARK_FAMILY: ReadonlySet<Theme> = new Set<Theme>(["dark", "startup"]);

// Escala de fonte (A-/A+): font-size do <html> em px. Como todo o design é rem,
// isto escala a UI inteira proporcionalmente (13–22px na spec; adotamos 14–20px
// com passo 1 para o dashboard). Default 16 (padrão do navegador).
export const FONT_MIN = 14;
export const FONT_MAX = 20;
export const FONT_DEFAULT = 16;
export const FONT_STEP = 1;

export const LANGUAGES = ["pt-BR", "es", "en"] as const;
export type Language = (typeof LANGUAGES)[number];
export const LANGUAGE_LABELS: Record<Language, string> = {
  "pt-BR": "Português (Brasil)",
  es: "Español",
  en: "English",
};
export type StudyMode = "traditional" | "interactive";

const systemTheme = (): Theme =>
  window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

const clampFont = (px: number): number =>
  Math.min(FONT_MAX, Math.max(FONT_MIN, Math.round(px)));

// Estado inicial do tema: lê o data-theme já aplicado pelo script inline (anti-FOUC),
// caindo para a classe .dark, e por fim "light".
function initialTheme(): Theme {
  const attr = document.documentElement.dataset.theme as Theme | undefined;
  if (attr && THEMES.includes(attr)) return attr;
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

// Estado inicial da fonte: o script inline já aplicou o font-size no <html>.
function initialFontScale(): number {
  const px = parseFloat(document.documentElement.style.fontSize);
  return Number.isFinite(px) ? clampFont(px) : FONT_DEFAULT;
}

interface PreferencesState {
  theme: Theme;
  themePreference: Theme | null;
  organizationTheme: Theme | null;
  setOrganizationTheme: (theme: Theme | null) => void;
  useOrganizationTheme: () => void;
  fontScale: number;
  language: Language;
  studyMode: StudyMode;
  // Papel que o usuário está "vendo como" (override do próprio papel), ou null.
  viewAs: OrgRole | null;
  // "Não perguntar de novo" na confirmação de exportar com filtros.
  skipExportFilterConfirm: boolean;
  setTheme: (theme: Theme) => void;
  cycleTheme: () => void;
  setFontScale: (px: number) => void;
  increaseFont: () => void;
  decreaseFont: () => void;
  resetFont: () => void;
  setLanguage: (language: Language) => void;
  setStudyMode: (mode: StudyMode) => void;
  setViewAs: (role: OrgRole | null) => void;
  setSkipExportFilterConfirm: (skip: boolean) => void;
}

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      theme: initialTheme(),
      themePreference: null,
      organizationTheme: null,
      fontScale: initialFontScale(),
      language: "pt-BR",
      studyMode: "traditional",
      viewAs: null,
      skipExportFilterConfirm: false,
      setTheme: (theme) => set({ theme, themePreference: theme }),
      setOrganizationTheme: (organizationTheme) =>
        set({
          organizationTheme,
          theme: get().themePreference ?? organizationTheme ?? systemTheme(),
        }),
      useOrganizationTheme: () =>
        set({
          themePreference: null,
          theme: get().organizationTheme ?? systemTheme(),
        }),
      cycleTheme: () => {
        const i = THEMES.indexOf(get().theme);
        get().setTheme(THEMES[(i + 1) % THEMES.length]!);
      },
      setFontScale: (px) => set({ fontScale: clampFont(px) }),
      increaseFont: () => set({ fontScale: clampFont(get().fontScale + FONT_STEP) }),
      decreaseFont: () => set({ fontScale: clampFont(get().fontScale - FONT_STEP) }),
      resetFont: () => set({ fontScale: FONT_DEFAULT }),
      setLanguage: (language) => set({ language }),
      setStudyMode: (studyMode) => set({ studyMode }),
      setViewAs: (viewAs) => set({ viewAs }),
      setSkipExportFilterConfirm: (skipExportFilterConfirm) => set({ skipExportFilterConfirm }),
    }),
    {
      name: "edu.preferences",
      // Preferências visuais sobrevivem ao reload; impersonação não. O `merge`
      // também limpa valores de viewAs gravados por versões anteriores.
      partialize: (state) => ({
        themePreference: state.themePreference,
        fontScale: state.fontScale,
        language: state.language,
        studyMode: state.studyMode,
        skipExportFilterConfirm: state.skipExportFilterConfirm,
      }),
      merge: (persisted, current) => {
        const saved = persisted as Partial<PreferencesState> | null;
        // O formato anterior salvava theme; preserva essa escolha na migração.
        const candidate =
          saved && "themePreference" in saved ? saved.themePreference : saved?.theme;
        const themePreference = candidate && THEMES.includes(candidate) ? candidate : null;
        return {
          ...current,
          themePreference,
          theme: themePreference ?? current.theme,
          fontScale:
            typeof saved?.fontScale === "number" && Number.isFinite(saved.fontScale)
              ? clampFont(saved.fontScale)
              : current.fontScale,
          language:
            saved?.language && LANGUAGES.includes(saved.language) ? saved.language : "pt-BR",
          studyMode: saved?.studyMode === "interactive" ? "interactive" : "traditional",
          viewAs: null,
          skipExportFilterConfirm: saved?.skipExportFilterConfirm === true,
        };
      },
    },
  ),
);
